import React, { useState, useEffect } from 'react';
import Product from '../Product';
import Collapsible from 'react-collapsible';
import {ProductGroup, NeuroMessage, ProductPagePropTypes, useNeuroMessages, usePages, buildPageUrl } from '@ignitesales/neuro-application/src/neuro-guide';
import { AnswersList } from '@ignitesales/neuro-application/src/neuro-guide/guide-framework';
import ContactFormModal from '../modals/ContactFormModal';
import SuccessFormModal from '../modals/SuccessFormModal';
import ShareViaEmailModal from '../modals/ShareViaEmailModal';
import ShareViaEmailComplete from '../modals/ShareViaEmailComplete';
import ShareViaSmsModal from '@ignitesales/neuro-application/src/neuro-guide/components/ShareViaSmsModal';
import PrintButton from '@ignitesales/neuro-application/src/neuro-guide/components/PrintButton';
import { useUser } from '@ignitesales/neuro-application/src/neuro-guide/hooks';
import { sendResultsViaEmail, triggerIgniteAction, useDispatch } from '@ignitesales/neuro-application/src/neuro-guide/state';
import * as analytics from  "../../utils/analytics";
import FontAwesome from 'react-fontawesome';

const ContactUsNoMatchesProduct = () => (
  <Product
    product={{
      id: '0000',
      name: 'Contact Us',
      addOns: [],
      groupType: 'No Product Recommendation',
      products: []
    }}
    onClick={() => window.open('https://www.rcu.org/contact', '_blank').focus()}
  />
);

function SummaryPage({ componentClass, productsList, productGroups }) {
  const { goToPreviousPage } = usePages();
  const { uuid } = useUser();
  const [ showContactFormModal, setShowContactFormModal ] = useState(false);
  const [ showShareViaSmsModal, setShowShareViaSmsModal ] = useState(false);
  const [ showShareViaEmailModal, setShowShareViaEmailModal ] = useState(false);
  const [ showShareViaEmailComplete, setShowShareViaEmailComplete ] = useState(false);
  const [ showSuccessFormModal, setShowSuccessFormModal ] = useState(false);
  const [ selectedProduct, setSelectedProduct ] = useState({});
  const [ leadTransmitted, setLeadTransmitted ] = useState(false);
  const [ onEmailSubmission, setOnEmailSubmission ] = useState(false);
  const [ collapsibleOpen, setCollapsibleOpen ] = useState(false);
  const dispatch = useDispatch();
  const messageContext = useNeuroMessages();
  const startOverUrl = buildPageUrl({ type: 'newUser' });
  
  const hasOpportunities = !!productsList?.length;

  useEffect(() => {
    const script = document.createElement('script');
    script.type = "text/javascript";
    script.src = "https://cdn.timetrade.com/scripts/lightbox/latest/lightbox.js"
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    async function saveAnswer() {

      const products = productsList.map(x => x.id).join('|');

      await ignite.answerService.saveAnswer({
        appkey: 'has_opportunities_products',
        value: products.length === 0 ? '' : products,
        timestamp: new Date()
      });
    }
    saveAnswer();
  }, [productsList]);

  const onScheduleClick = (timetradeLink) => {
    const guideUrl  = window.location.href;
    analytics.processEvent({ event: 'scheduleAppointment', data: { guideUrl } });
    dispatch(triggerIgniteAction({ actionName: 'scheduleAppointmentClicked', value: 'started' }));
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 800;
    const anchor = document.createElement('a');
    anchor.href = timetradeLink || "https://www04.timetrade.com/app/royalcu/workflows/WFroyalcu001/schedule";
    anchor.target = "_blank";
    anchor.onclick = "return tt.launchWorkflow(this); ";
    return isMobile ? anchor.click() : tt.launchWorkflow(anchor);
  };

  const hasTimetradeLink = ( productParam ) => /timetrade\.com/.test(productParam.productUrl);

  const renderProductGroup = (productGroup, subGroups, products) => {
    const cssBEM = 'product-group';

    return products.length ?
      <div className={cssBEM} key={ productGroup.name }>
        <div className={ `${ cssBEM }__container` } key={ productGroup.name }>
          <div className={ `${ cssBEM }__header count-${ products.length || 0 }` }>
            <h1><NeuroMessage id={ `productGroup.${ productGroup.name }` } tagName="span"/></h1>
          </div>
          { subGroups || null }
          { products || null }
        </div>
      </div> : null;
  };

  const openProductUrl = () => window.open( selectedProduct.productUrl,  "_blank" );

  const onProductClick = async (params) => {
    setSelectedProduct(params);
    if(hasTimetradeLink(params)) {
      const isNonProd = ['development','staging','pilot'].includes(window.ignite.getRunMode());
      const link = messageContext.getMessage(`${params.productId}.actionType.url${isNonProd ? '.test' : ''}`);
      onScheduleClick(link);
    } else {
      let currentLeadTransmitted = leadTransmitted;
      if (window.ignite && window.ignite.answerService) {
        try {
          const submissionStatus = await window.ignite.answerService.getAnswer('contactFormSubmitted');
          if (submissionStatus && submissionStatus.value[0] === 'true') {
            currentLeadTransmitted = true;
          } else {
            currentLeadTransmitted = false;
          }
        } catch (error) {
          console.error('Error fetching contact form submission status in onProductClick:', error);
          currentLeadTransmitted = false;
        }
        setLeadTransmitted(currentLeadTransmitted);
      }

      if(currentLeadTransmitted) {
        setShowSuccessFormModal(true);
      } else {
        setShowContactFormModal(true);
      }
    }
  };

  const onContactSuccess = (lead) => {
    setShowContactFormModal(false);
    setShowSuccessFormModal(true);
    setLeadTransmitted(true);

    if (window.ignite && window.ignite.answerService) {
      try {
        window.ignite.answerService.saveAnswer({
          appkey: 'contactFormSubmitted',
          value: 'true',
          timestamp: new Date()
        });
      } catch (error) {
        console.error('Error saving contact form submission status:', error);
      }
    }

    const sendEmailParams = {
      email: lead.email,
      fromEmail: null,
      copyMe: false,
    };

    if( !onEmailSubmission ) {
      dispatch(sendResultsViaEmail(sendEmailParams));
    }
  };

  const onSuccessAction = (e) => {
    e.preventDefault();
    openProductUrl();
    setShowSuccessFormModal(false);
  };

  const onEmailComplete = () => {
    setOnEmailSubmission(true);
  };

  const renderProduct = (product) => {
    return <Product
      key={ product.id }
      product={ product }
      onClick={ onProductClick }
    />;
  };

  const contactFormModal = (
    <ContactFormModal
      className={`${componentClass}__contactModal contactForm`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showContactFormModal}
      onClose={() => setShowContactFormModal(false)}
      onComplete={onContactSuccess}
      selectedProduct={selectedProduct}
    />
  );

  const successFormModal = (
    <SuccessFormModal
      className={ `${componentClass}__successFormModal` }
      closeBtn={ <i className={'fa fa-times'} /> }
      isOpen={ showSuccessFormModal }
      onClose={ () => setShowSuccessFormModal(false) }
      onSuccessModalClose={ onSuccessAction }
      selectedProduct= { selectedProduct }
    />
  );

  const shareViaSmsModal = (
    <ShareViaSmsModal
      className={`${componentClass}__smsModal shareModal`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showShareViaSmsModal}
      onClose={() => setShowShareViaSmsModal(false)}
    />
  );

  const shareViaEmailModal = (
    <ShareViaEmailModal
      className={`${componentClass}__emailModal shareModal`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showShareViaEmailModal}
      onClose={() => setShowShareViaEmailModal(false)}
      onEmailComplete={onEmailComplete}
    />
  );

  const shareViaEmailComplete = (
    <ShareViaEmailComplete
      className={`${componentClass}__emailModal emailComplete`}
      closeBtn={<i className={'fa fa-times'} />}
      isOpen={showShareViaEmailComplete}
      onClose={() => setShowShareViaEmailComplete(false)}
    />
  );

  const actionItems = <div className="actions">
    <div className="sms action" onClick={() => { setShowShareViaSmsModal(true); }}>
      <span className="fa fa-mobile"/>
      <NeuroMessage id="summary.mobile" tagName='h5' />
    </div>

    <div className="email action" onClick={ onEmailSubmission || leadTransmitted ? () => { setShowShareViaEmailComplete(true); } : () => { setShowShareViaEmailModal(true); } }>
      <span className="fa fa-envelope"/>
      <NeuroMessage id="summary.email" tagName='h5' />
    </div>

    <PrintButton className="print action" tagName="div">
      <span className="fa fa-print"/>
      <NeuroMessage id="summary.print" tagName='h5' />
    </PrintButton>
  </div>

  const header = <div className={ `${ componentClass }__collapsible` }>
    <span>
      See my answers
      {
        collapsibleOpen
          ? <i class="fa fa-chevron-down"></i>
          : <i className="fa fa-chevron-right"></i>
      }
    </span>
  </div>;

  return (
    <div className={componentClass}>
      <div className={ `${ componentClass }__header` }>
        <NeuroMessage id="summary.header" tagName='h1' />
      </div>

      <div className={ `${ componentClass }__actionBar` }>
        <div className="guideInfo">
          <h4>Reference #{ uuid }</h4>
          <NeuroMessage id="summary.actionBar" tagName='p' values={{ uuid }} />
        </div>
        { actionItems }
      </div>

      <div className={ `${ componentClass }__products` }>{ productGroups.map(g =>
        <ProductGroup group={ g } renderGroupFn={ renderProductGroup } renderProductFn={ renderProduct } key={ g.name } />
      )}
      {
        !hasOpportunities &&
        <div className="product-group">
          <div className="product-group__container">
            <div className='contact-us-no-product-matches'>
              <ContactUsNoMatchesProduct />
            </div>
          </div>
        </div>
      }
      </div>

      <div className={ `${ componentClass }__actionBarCollapsible` }>
        { actionItems }

      </div>

      <div className={ `${ componentClass }__scheduleAppointment` }>
        <button className="btn"
          onClick={ () => onScheduleClick() }>
          <NeuroMessage id={ 'summary.schedule' } />
        </button>
      </div>

      <Collapsible
        trigger={header}
        onOpening={() => setCollapsibleOpen(true)}
        onClose={() => setCollapsibleOpen(false)}
      >
        <div className={ `${ componentClass }__answers` }>
          <NeuroMessage id="summary.answers" tagName='h2' />
          {componentClass !== 'detailsPage' && 
            <div className="warning-container">
              <FontAwesome name="exclamation-triangle" className="warning-icon" />
              <NeuroMessage id="summary.answersWarning" tagName='div' className="warning" />
            </div>
          }
          <AnswersList linkToQuestion />
        </div>
      </Collapsible>

      <div className={ `${ componentClass }__navigation` }>
        {componentClass === 'detailsPage' ?
          <a href={startOverUrl} className="btn">
            <NeuroMessage id="startNewGuide" tagName="span" />
          </a>
        :
          <button onClick={goToPreviousPage} className="btn btn-secondary"><NeuroMessage id="nav.prev"/></button>
        }
      </div>
      <div className={ `${ componentClass }__footer` }>
        <NeuroMessage id="summary.footer" tagName='p'/>
      </div>

      { contactFormModal }
      { shareViaSmsModal }
      { shareViaEmailModal }
      { shareViaEmailComplete }
      { successFormModal }

    </div>
  );
}

SummaryPage.propTypes = ProductPagePropTypes;

export default SummaryPage;
